<script setup>
import moment from 'moment-timezone'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useReservationsStore } from '@/stores'
import { numberWithCommas } from '@/plugins/misc'
import { requiredValidator } from '@validators'
import { budgetFlexOptions, budgetQuestion } from '@/utils/inquiryCopy'

const props = defineProps({
  selectedRestaurantNames: { type: String, default: '' },
  selectedRestaurantsMinSpend: { type: Array, default: () => [] },
  minSpendOptions: { type: Array, default: () => [] },
  selectedRestaurantsNotAcceptingReferrals: { type: Array, default: null },
  saving: { type: Boolean, default: false },
})

const emit = defineEmits(['next', 'change', 'scroll-to-selected'])

const reservationsStore = useReservationsStore()
const { editingReservation } = storeToRefs(reservationsStore)

const form = ref()
const valid = ref(true)

// 10/15: the answers carry the chosen amount — "Yes, $150pp is my max" / "No, can go higher if needed"
const flexOptions = computed(() => budgetFlexOptions(editingReservation.value?.minSpendPerPerson))

const markChanged = () => emit('change')
</script>

<template>
  <div class="tab-content occasion-step">
    <VForm
      ref="form"
      v-model="valid"
    >
      <div class="occasion-context">
        <div class="occasion-context-name">
          {{ selectedRestaurantNames }}
        </div>
        <div class="occasion-context-details">
          {{ editingReservation.date ? moment(editingReservation.date).format('ddd, MMM D') : '' }}
          <template v-if="editingReservation.preferredTime">
            · {{ editingReservation.preferredTime }}
          </template>
          <template v-if="editingReservation.partySize">
            · {{ editingReservation.partySize }} {{ editingReservation.partySize == 1 ? 'guest' : 'guests' }}
          </template>
        </div>
      </div>

      <div
        class="occasion-spend-card"
        @click="emit('scroll-to-selected'); markChanged()"
        @keyup="markChanged"
      >
        <div class="occasion-spend-card-header">
          F&B Budget
        </div>
        <div class="occasion-spend-hint">
          {{ $t('reservation.private.higherBudgetExpedite') }}
        </div>
        <template v-if="selectedRestaurantsMinSpend?.length > 0">
          <div
            v-for="(minSpendObject, index) in selectedRestaurantsMinSpend"
            :key="index"
            class="occasion-spend-hint"
          >
            ${{ minSpendObject.minSpend != null && minSpendObject.minSpend !== undefined ? (minSpendObject.minSpend === 0 ? '0' : numberWithCommas(minSpendObject.minSpend)) : '0' }}pp minimum for {{ minSpendObject.name }}
          </div>
        </template>
        <div class="occasion-spend-card-body">
          <VSelect
            v-model="editingReservation.minSpendPerPerson"
            class="min-spend-per-person-select occasion-spend-select"
            :label="$t('reservation.payment.budgetPerPersonLabel')"
            item-title="text"
            item-value="value"
            :items="minSpendOptions"
            :rules="[requiredValidator]"
            required
          />
          <div
            v-if="editingReservation.minSpendPerPerson"
            class="occasion-spend-attribution"
            style="font-weight: 600; color: #1a1a1a; font-size: 14px;"
          >
            ${{ numberWithCommas(editingReservation.partySize * editingReservation.minSpendPerPerson) }} total (${{ numberWithCommas(editingReservation.minSpendPerPerson) }}pp)
          </div>
          <div class="occasion-spend-attribution">
            {{ $t('reservation.payment.preTaxTip') }}
          </div>
        </div>
      </div>

      <div
        v-if="editingReservation.minSpendPerPerson"
        class="occasion-chips-section budget-flex-section"
        @click="markChanged"
      >
        <div class="occasion-chips-label">
          {{ budgetQuestion(editingReservation.minSpendPerPerson) }}
        </div>
        <div class="occasion-chips-grid">
          <button
            v-for="flex in flexOptions"
            :key="flex.value"
            type="button"
            class="occasion-chip"
            :class="{ 'occasion-chip--active': editingReservation.budgetFlexibility === flex.value }"
            @click="editingReservation.budgetFlexibility = flex.value"
          >
            {{ flex.label }}
          </button>
        </div>
        <VSelect
          v-model="editingReservation.budgetFlexibility"
          :items="flexOptions"
          item-title="label"
          item-value="value"
          :rules="[requiredValidator]"
          required
          class="occasion-validation-select"
        />
      </div>

      <div
        v-if="(editingReservation.minSpendPerPerson * editingReservation.partySize < 1000)"
        class="text-caption text-center mt-2"
        style="color: #e65100;"
      >
        {{ $t('reservation.payment.minBudgetRequired') }}
      </div>

      <div
        v-if="editingReservation.reservationType == 'private'"
        class="text-center mt-4 mb-2"
      >
        <VBtn
          :disabled="!!selectedRestaurantsNotAcceptingReferrals || !editingReservation.minSpendPerPerson || !editingReservation.budgetFlexibility || saving || (editingReservation.minSpendPerPerson * editingReservation.partySize < 1000)"
          color="primary-dark"
          size="large"
          block
          class="occasion-next-btn"
          @click="emit('next')"
        >
          Next
        </VBtn>
      </div>
    </VForm>
  </div>
</template>

<style lang="scss" scoped>
$text-dark: var(--ds-text-dark);
$text-medium: var(--ds-text-medium);
$text-light: var(--ds-text-light);

.tab-content {
  position: relative;
}

.occasion-step {
  padding: 0 20px 8px;
  max-width: 420px;
  margin: 0 auto;
}

.occasion-context {
  text-align: center;
  padding: 16px 0 8px;
}

.occasion-context-name {
  font-size: 18px;
  font-weight: 700;
  color: $text-dark;
  line-height: 1.3;
}

.occasion-context-details {
  font-size: 13px;
  color: $text-medium;
  margin-top: 2px;
}

.occasion-chips-section {
  padding: 12px 0 4px;
}

.occasion-chips-label {
  font-size: 14px;
  font-weight: 600;
  color: $text-dark;
  margin-bottom: 10px;
}

.occasion-chips-hint {
  font-size: 13px;
  color: $text-medium;
  margin: -6px 0 10px;
}



.occasion-chips-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.occasion-chip {
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: $text-dark;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;

  &:hover {
    border-color: #bbb;
  }

  &--active {
    border-color: var(--ds-brand-primary-dark);
    background: var(--ds-brand-primary-light);
    color: var(--ds-brand-primary-dark);
    font-weight: 600;
  }
}

.occasion-validation-select {
  :deep(.v-input__control) {
    display: none;
  }

  :deep(.v-input__details) {
    padding-top: 0;
    min-height: 0;
  }

  &:not(.v-input--error) {
    display: none;
  }
}

.occasion-spend-card {
  background: #f8f8f9;
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
}

.occasion-spend-card-header {
  font-size: 14px;
  font-weight: 600;
  color: $text-dark;
  text-align: center;
  margin-bottom: 12px;
}

.occasion-spend-card-body {
  max-width: 300px;
  margin: 0 auto;
}

.occasion-spend-hint {
  font-size: 13px;
  color: $text-light;
  text-align: center;
  margin-bottom: 8px;
}

.occasion-spend-select {
  :deep(.v-field) {
    background: #fff !important;
    border: 1.5px solid #e0e0e0;
    border-radius: 12px;
  }

  :deep(.v-field:hover) {
    border-color: #bbb;
  }

  :deep(.v-field--focused) {
    border-color: var(--ds-brand-primary-dark);
  }
}

.occasion-spend-attribution {
  font-size: 12px;
  color: $text-medium;
  text-align: center;
  margin-top: 4px;
}

.occasion-next-btn {
  max-width: 340px;
  margin: 0 auto;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.min-spend-per-person-select :deep(.v-field__field) {
  white-space: unset;
  box-sizing: content-box;
  padding-top: 6px;
}
</style>
